import { ApolloQueryResult } from '@apollo/client/index.js';
import { GetMePatientQuery, useGetMePatientQuery } from '@pm/graphql';
import {
  createContext,
  PropsWithChildren,
  use,
  useEffect,
  type FC,
} from 'react';
import { LoadingPage } from '../pages/LoadingPage';

type PatientProfileContextType = {
  profile: NonNullable<Required<GetMePatientQuery>['me']>;
  refetch: () => Promise<ApolloQueryResult<GetMePatientQuery>>;
  loading: boolean;
};

export const PatientProfileContext = createContext<
  PatientProfileContextType | undefined
>(undefined);

export const usePatientProfileContext = () => use(PatientProfileContext);

export const PatientProfileProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, refetch, loading } = useGetMePatientQuery();

  // These three feed LaunchDarkly's user context via LDUserLocation, which is a
  // later sibling of the app tree, so its effect runs after this one and sees
  // the written values. Writing them here rather than during render also means
  // HAS_RECEIVED_RX is no longer set to "false" on the passes before the query
  // resolves.
  useEffect(() => {
    if (!data?.me) return;

    const provinceCode = data.me.profile?.province?.code;
    if (provinceCode) {
      localStorage.setItem('PROVINCE', provinceCode);
    }

    const hasReceivedRX = (data.me.workflowSteps ?? []).some(
      (step) => step.type === 'Steps::FulfillPrescription',
    );
    sessionStorage.setItem('HAS_RECEIVED_RX', hasReceivedRX.toString());

    if (data.me.id) {
      sessionStorage.setItem('USER_ID', data.me.id);
    }
  }, [data]);

  if (!data?.me) {
    return loading ? <LoadingPage /> : null;
  }

  return (
    <PatientProfileContext
      value={{
        profile: data.me,
        refetch,
        loading,
      }}
    >
      {children}
    </PatientProfileContext>
  );
};
